@import '../../../../design-tokens/dist/private/colors';
@import '../../../../style/src/typography/mixins';
@import '../../mixins/outline';
@import 'mixins';

@keyframes countdown-background-animation {
	0% {
		width: 0;
	}

	100% {
		width: 100%;
	}
}

:host {
	@include zds-typography('body');

	// Modified insets
	--zds-button-inset-x-modified: var(--zds-button-inset-x);
	--zds-button-inset-y-modified: var(--zds-button-inset-y);

	// Focus outlines
	--zds-button-contained-outline-color: var(--zds-button-outlined-border-color);
	--zds-button-outlined-outline-color: var(--zds-button-outlined-border-color);
	--zds-button-text-outline-color: var(--zds-button-text-color);

	// Icon offset
	--zds-button-icon-offset: var(--zds-space-3);

	// Set default references for internal CSS variables, used for presets without design tokens
	--zds-button-outlined-border-color: var(--zds-button-contained-background);
	--zds-button-outlined-border-color-hover: var(--zds-button-contained-background-hover);
	--zds-button-outlined-color: var(--zds-button-contained-background);
	--zds-button-text-background-hover: var(--zds-button-outlined-background-hover);
	--zds-button-text-color: var(--zds-button-contained-background);
	--zds-button-text-color-hover: var(--zds-button-contained-background-hover);

	// Used for white button hover state
	--zds-button-contained-background-opacity-hover: 1;

	display: inline-block;
}

:host([size='sm']) {
	--zds-button-icon-offset: var(--zds-space-2);
	--zds-button-inset-x-modified: calc(var(--zds-button-inset-x) * 0.75);
	--zds-button-inset-y-modified: calc(var(--zds-button-inset-y) * 0.75);
}

:host([size='lg']) {
	--zds-button-inset-x-modified: calc(var(--zds-button-inset-x) * 1.5);
	--zds-button-inset-y-modified: calc(var(--zds-button-inset-y) * 1.5);
}

:host([aspect-square]) {
	--zds-button-inset-x-modified: var(--zds-button-inset-y-modified);
}

:host([pill]) {
	--zds-button-border-radius: var(--zds-shape-radius-pill);
}

:host([fit-height]) {
	height: 100%;
}

// Define all internal CSS variables to handle colour presets
@include apply-all-presets;

.zds-button {
	$this: &;

	background: none;
	border: 0;
	border-radius: var(--zds-button-border-radius); // NB: border radius can be modified by JS
	box-shadow: var(--zds-button-shadow);
	cursor: pointer;
	display: block;
	font-family: var(--zds-button-font-family);
	font-style: var(--zds-button-font-style);
	font-weight: var(--zds-button-font-weight);
	margin: 0; // Remove unnecessary margin in Safari
	min-width: 100%;
	padding: var(--zds-button-inset-y-modified) var(--zds-button-inset-x-modified);
	position: relative;
	text-decoration: none;
	text-transform: var(--zds-button-text-transform);
	transition:
		background-color var(--zds-transition-md),
		border-color var(--zds-transition-md),
		color var(--zds-transition-md),
		transform var(--zds-transition-md); // Note: there should be no transition on the outline

	&:not(&--ready) {
		visibility: hidden;
	}

	&:hover {
		transform: scale(var(--zds-button-scale-hover));
	}

	&:focus-visible {
		outline: 0;
	}

	&:disabled {
		opacity: 0.5;
		pointer-events: none;
	}

	// Show active state when :active or when a click was just performed
	// Additional .zds-button class is to increase specificity
	&:active,
	&--was-just-clicked.zds-button {
		&,
		&:hover,
		&:focus-visible {
			transform: scale(calc(0.95 * var(--zds-button-scale-hover)));
		}
	}

	&--aspect-square {
		aspect-ratio: 1;
	}

	&--fit-height {
		height: 100%;
	}

	&--overhang-bottom {
		margin-bottom: calc(-1 * var(--zds-button-inset-y-modified));
	}

	&--overhang-left {
		margin-left: calc(-1 * var(--zds-button-inset-x-modified));
	}

	&--overhang-right {
		margin-right: calc(-1 * var(--zds-button-inset-x-modified));
	}

	&--overhang-top {
		margin-top: calc(-1 * var(--zds-button-inset-y-modified));
	}

	&__wrapper {
		align-items: center;
		display: flex;
		justify-content: center;
		min-height: 1.5em; // Equal to line height; necessary to enforce line-height when button contains only an icon
		position: relative;
	}

	&--aspect-square &__wrapper {
		// Match min-height to avoid aspect ratio issues
		min-width: 1.5em;
	}

	&__background,
	&__background-hover,
	&__border,
	&__countdown-background {
		border-radius: inherit;
		inset: 0;
		position: absolute;
		transition:
			opacity var(--zds-transition-md),
			filter var(--zds-transition-md);
	}

	&__background-hover {
		opacity: 0;

		#{$this}:hover &,
		#{$this}:focus-visible & {
			opacity: 1;
		}
	}

	&__border {
		border-radius: inherit;
	}

	&__countdown-background {
		display: none;
		overflow: hidden;

		#{$this}--auto-submit & {
			display: block;
		}

		#{$this}:hover &,
		#{$this}--variant-contained:focus-visible & {
			filter: brightness(0.7);
		}
	}

	&__countdown-background-inner {
		animation: countdown-background-animation linear;
		animation-fill-mode: forwards;
		background: var(--zds-button-contained-background-hover, transparent);
		height: 100%;
		width: 0;
	}

	/* Slotted icons */
	// Note: these classes are placed on slotted icons via JS

	::slotted(.zds-button__icon) {
		// Ensure that clicks on the icon still give focus to the button
		pointer-events: none;
	}

	::slotted(.zds-button__icon--has-content-after) {
		margin-right: var(--zds-button-icon-offset);
	}

	::slotted(.zds-button__icon--has-content-before) {
		margin-left: var(--zds-button-icon-offset);
	}
}

/* Button variants */

.zds-button--variant {
	&-contained,
	&-outlined {
		.zds-button__border {
			border-style: solid;
			border-width: var(--zds-button-border-width);
		}
	}

	&-contained {
		@include outline;

		--zds-outline-color: var(--zds-button-contained-outline-color, transparent);

		color: var(--zds-button-contained-color, inherit);

		.zds-button__background {
			background: var(--zds-button-contained-background, transparent);
		}

		.zds-button__background-hover {
			background: var(--zds-button-contained-background-hover, transparent);
		}

		.zds-button__border {
			border-color: var(--zds-button-contained-border-color, transparent);
		}

		&:hover,
		&:focus-visible {
			color: var(--zds-button-contained-color-hover, inherit);
			transform: scale(var(--zds-button-scale-hover));

			.zds-button__background,
			.zds-button__background-hover {
				opacity: var(--zds-button-contained-background-opacity-hover, 1);
			}

			.zds-button__border {
				border-color: var(--zds-button-contained-border-color-hover, transparent);
			}
		}
	}

	&-outlined {
		@include outline;

		--zds-outline-color: var(--zds-button-outlined-outline-color, transparent);

		color: var(--zds-button-outlined-color, inherit);

		.zds-button__background-hover {
			background: var(--zds-button-outlined-background-hover, transparent);
		}

		.zds-button__border {
			border-color: var(--zds-button-outlined-border-color, transparent);
		}

		&:hover,
		&:focus-visible {
			color: var(--zds-button-outlined-color-hover, inherit);

			.zds-button__border {
				border-color: var(--zds-button-outlined-border-color-hover, transparent);
			}
		}
	}

	&-text {
		@include outline;

		--zds-outline-color: var(--zds-button-text-outline-color, transparent);

		background: transparent;
		border-color: transparent;
		color: var(--zds-button-text-color, inherit);

		.zds-button__background-hover {
			background: var(--zds-button-text-background-hover, transparent);
		}

		&:hover,
		&:focus-visible {
			background: transparent;
			border-color: transparent;
			color: var(--zds-button-text-color-hover, inherit);
		}
	}
}

/* Button sizes */

.zds-button--size {
	&-sm {
		font-size: var(--zds-font-size-sm);
	}

	&-md {
		font-size: var(--zds-font-size-md);
	}

	&-lg {
		font-size: var(--zds-font-size-lg);
	}
}

:host([preset='inherit']) {
	background: inherit;
	color: inherit;
}
